import { Box, Container, Grid, Typography, Link, Stack, Divider } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useSettings } from '../../hooks/usePublicApi';

const footerNav = [
  { label: 'Home', path: '/' },
  { label: 'Referenzen', path: '/referenzen' },
  { label: 'Karriere', path: '/karriere' },
  { label: 'Über uns', path: '/uber-uns' },
  { label: 'Anfrage', path: '/anfrage' },
];

const footerServices = [
  { label: 'Neubau', path: '/neubau' },
  { label: 'Umbau', path: '/umbau' },
  { label: 'Sanierung', path: '/sanierung' },
];

const footerLegal = [
  { label: 'Impressum', path: '/impressum' },
  { label: 'Datenschutz', path: '/datenschutz' },
  { label: 'AGB', path: '/agb' },
];

const defaultContact = {
  company: 'Süd Ausbau GmbH',
  address: 'Heiterwanger Straße 64, 81373 München',
  phone: '+49 (0) 89 32402433',
  email: 'info@sued-ausbau.de',
  tagline: 'Ihr Partner für hochwertigen Innenausbau, Umbau und Sanierung in München und Umgebung.',
};

export default function Footer() {
  const { settings } = useSettings();
  const company = settings?.company?.name || defaultContact.company;
  const contactAddr = settings?.contact?.address;
  const address = contactAddr
    ? [contactAddr.street, `${contactAddr.zip || ''} ${contactAddr.city || ''}`.trim()].filter(Boolean).join(', ')
    : defaultContact.address;
  const phone = settings?.contact?.phone || defaultContact.phone;
  const email = settings?.contact?.email || defaultContact.email;
  const tagline = settings?.company?.slogan || defaultContact.tagline;

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: 'secondary.dark',
        color: 'white',
        pt: { xs: 6, md: 8 },
        pb: 4,
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Company */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography variant="h5" sx={{ color: 'primary.main', mb: 2, fontWeight: 700 }}>
              {company}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 3, maxWidth: 280 }}>
              {tagline}
            </Typography>
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <LocationOnIcon sx={{ color: 'primary.main', fontSize: 18 }} />
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                  {address}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <PhoneIcon sx={{ color: 'primary.main', fontSize: 18 }} />
                <Link
                  href={`tel:${phone.replace(/[\s()]/g, '')}`}
                  sx={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none', '&:hover': { color: 'primary.main' } }}
                  variant="body2"
                >
                  {phone}
                </Link>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <EmailIcon sx={{ color: 'primary.main', fontSize: 18 }} />
                <Link
                  href={`mailto:${email}`}
                  sx={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none', '&:hover': { color: 'primary.main' } }}
                  variant="body2"
                >
                  {email}
                </Link>
              </Stack>
            </Stack>
          </Grid>

          {/* Navigation */}
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <Typography variant="subtitle2" sx={{ color: 'primary.main', mb: 2, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.75rem' }}>
              Navigation
            </Typography>
            <Stack spacing={1}>
              {footerNav.map((item) => (
                <Link
                  key={item.path}
                  component={RouterLink}
                  to={item.path}
                  sx={{
                    color: 'rgba(255,255,255,0.6)',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    '&:hover': { color: 'primary.main' },
                    transition: 'color 0.2s',
                  }}
                >
                  {item.label}
                </Link>
              ))}
            </Stack>
          </Grid>

          {/* Leistungen */}
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <Typography variant="subtitle2" sx={{ color: 'primary.main', mb: 2, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.75rem' }}>
              Leistungen
            </Typography>
            <Stack spacing={1}>
              {footerServices.map((item) => (
                <Link
                  key={item.path}
                  component={RouterLink}
                  to={item.path}
                  sx={{
                    color: 'rgba(255,255,255,0.6)',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    '&:hover': { color: 'primary.main' },
                    transition: 'color 0.2s',
                  }}
                >
                  {item.label}
                </Link>
              ))}
            </Stack>
          </Grid>

          {/* Rechtliches */}
          <Grid size={{ xs: 12, sm: 4, md: 2 }}>
            <Typography variant="subtitle2" sx={{ color: 'primary.main', mb: 2, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.75rem' }}>
              Rechtliches
            </Typography>
            <Stack spacing={1}>
              {footerLegal.map((item) => (
                <Link
                  key={item.path}
                  component={RouterLink}
                  to={item.path}
                  sx={{
                    color: 'rgba(255,255,255,0.6)',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    '&:hover': { color: 'primary.main' },
                    transition: 'color 0.2s',
                  }}
                >
                  {item.label}
                </Link>
              ))}
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: 4 }} />

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
        >
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)' }}>
            &copy; {new Date().getFullYear()}{' '}
            <a
              href="https://www.jikarnajm.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'rgba(255,255,255,0.4)', textDecoration: 'none' }}
            >
              Zhikar Hamasaeed
            </a>
            . Alle Rechte vorbehalten.
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)' }}>
            Innenausbau · Umbau · Sanierung — München
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
}
